<template>
  <div class="recurrent-event-parameters">
    <div class="block every">
      <div class="line">
        <p>Repeat every</p>
        <input v-model="period" type="number" min="1" />
        <VSelect
          v-model="every"
          label="every"
          :clearable="false"
          :searchable="false"
          :reduce="
            (recurrence: Record<'id' | 'every', string>) => recurrence.id
          "
          :options="[
            { id: 'day', every: 'Day' },
            { id: 'week', every: 'Week' },
            { id: 'month', every: 'Month' },
            { id: 'year', every: 'Year' },
          ]"
          class="select"
        >
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes" />
          </template>
        </VSelect>
      </div>
    </div>
    <div v-if="recurrence.rule.every === ('week' || 'workday')" class="block">
      <p>Days of the week:</p>
      <div class="day-of-the-week">
        <div
          v-if="isWeekStartOnSunday"
          class="day"
          :class="{ clicked: weekdays.sunday }"
          @click="queryWeekday('sunday')"
        >
          S
        </div>
        <div
          class="day"
          :class="{ clicked: weekdays.monday }"
          @click="queryWeekday('monday')"
        >
          M
        </div>
        <div
          class="day"
          :class="{ clicked: weekdays.tuesday }"
          @click="queryWeekday('tuesday')"
        >
          T
        </div>
        <div
          class="day"
          :class="{ clicked: weekdays.wednesday }"
          @click="queryWeekday('wednesday')"
        >
          W
        </div>
        <div
          class="day"
          :class="{ clicked: weekdays.thursday }"
          @click="queryWeekday('thursday')"
        >
          T
        </div>
        <div
          class="day"
          :class="{ clicked: weekdays.friday }"
          @click="queryWeekday('friday')"
        >
          F
        </div>
        <div
          class="day"
          :class="{ clicked: weekdays.saturday }"
          @click="queryWeekday('saturday')"
        >
          S
        </div>
        <div
          v-if="!isWeekStartOnSunday"
          class="day"
          :class="{ clicked: weekdays.sunday }"
          @click="queryWeekday('sunday')"
        >
          S
        </div>
      </div>
    </div>
    <div
      v-if="recurrence.rule.every === 'month'"
      class="block day-of-the-month"
    >
      <VSelect
        v-model="dayOfTheMonth"
        label="dayOftheMonth"
        :clearable="false"
        :searchable="false"
        :reduce="(day: Record<'id' | 'dayOftheMonth', string>) => day.id"
        :options="[
          { id: eventDay, dayOftheMonth: `Every month on the ${eventDay}` },
          { id: 1, dayOftheMonth: 'The first day of the month' },
        ]"
        class="select"
      >
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes"><ChevronDownIcon /></span>
        </template>
      </VSelect>
    </div>
    <div class="block end">
      <p>Ends:</p>

      <div class="line">
        <input
          id="never"
          type="radio"
          name="end"
          checked
          @click="setEnd('never')"
        />
        <label for="never">never</label>
      </div>
      <div class="line disabled">
        <input
          id="date"
          type="radio"
          name="end"
          disabled
          @click="setEnd('date')"
        />
        <label for="date"
          >on <input v-model="dateEnd" type="date" disabled /> (coming
          soon)</label
        >
      </div>
      <div class="line disabled">
        <input
          id="occurrence"
          type="radio"
          name="end"
          disabled
          @click="setEnd('occurrence')"
        />
        <label for="occurrence"
          >after
          <input v-model="occurrencesEnd" type="number" min="1" />
          occurrence{{ occurrencesEnd > 1 ? "s" : "" }}</label
        >(coming soon)
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { getDate } from "date-fns";
import {
  computed,
  defineComponent,
  PropType,
  Ref,
  ref,
  toRefs,
  watch,
} from "vue";

import { Recurrence, Weekdays } from "@ctrl/controller-rpc";

import { getMonth } from "@/components/dates";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import { UseSettings } from "@/components/settings/controller/settings";

export default defineComponent({
  components: {
    ChevronDownIcon,
  },
  props: {
    eventDate: {
      type: Date,
      required: true,
    },
    recurrence: {
      type: Object as PropType<Recurrence>,
      required: true,
    },
  },
  emits: {
    "update:recurrence": (r: Recurrence) => {
      if (r) return true;
      else return false;
    },
  },
  setup(props, { emit }) {
    const { recurrence, eventDate } = toRefs(props);
    const eventDay = ref(getDate(eventDate.value));
    const { isWeekStartOnSunday } = UseSettings.getInstance();
    //period
    const period = computed({
      get() {
        return recurrence.value.rule.period || 1;
      },
      set(period: number) {
        if (period > 0) {
          // The parseInt(`number`) is a hack to prevent the emit to turn period to a string for some reason.
          emit("update:recurrence", {
            ...recurrence.value,
            rule: { ...recurrence.value.rule, period: parseInt(`${period}`) },
          });
        }
      },
    });
    // every
    const every = computed({
      get() {
        if (recurrence.value.rule.every === "workday") {
          emit("update:recurrence", {
            ...recurrence.value,
            rule: {
              period: period.value,
              weekdays: {
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
                saturday: false,
                sunday: false,
              },
              every: "week",
            },
          });
          return "week";
        }
        return recurrence.value.rule.every || "week";
      },
      set(every: "week" | "day" | "month" | "year") {
        switch (every) {
          case "day":
            emit("update:recurrence", {
              ...recurrence.value,
              rule: { period: period.value, every },
            });
            break;
          case "week":
            emit("update:recurrence", {
              ...recurrence.value,
              rule: { period: period.value, weekdays: weekdays.value, every },
            });
            break;
          case "month":
            emit("update:recurrence", {
              ...recurrence.value,
              rule: { period: period.value, day: dayOfTheMonth.value, every },
            });
            break;
          case "year":
            emit("update:recurrence", {
              ...recurrence.value,
              rule: {
                period: period.value,
                day: eventDay.value,
                month: getMonth(eventDate.value),
                every,
              },
            });
            break;
        }
      },
    });
    // Weekdays
    const weekdays = computed(
      () =>
        (recurrence.value.rule.every === "week" &&
          recurrence.value.rule.weekdays) || {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
        }
    );
    const queryWeekday = (day: keyof Weekdays) => {
      if (
        recurrence.value.rule.every === "week" &&
        recurrence.value.rule.weekdays
      ) {
        const trueCount = Object.values(recurrence.value.rule.weekdays).filter(
          (day) => day === true
        ).length;
        if (
          trueCount > 1 ||
          (trueCount === 1 && !recurrence.value.rule.weekdays[day])
        ) {
          emit("update:recurrence", {
            ...recurrence.value,
            rule: {
              ...recurrence.value.rule,
              weekdays: {
                ...recurrence.value.rule.weekdays,
                [day]: !recurrence.value.rule.weekdays[day],
              },
            },
          });
        }
      }
    };
    // Day of the month
    const dayOfTheMonth = computed({
      get() {
        return (
          (recurrence.value.rule.every === "month" &&
            recurrence.value.rule.day) ||
          eventDay.value
        );
      },
      set(day: number) {
        emit("update:recurrence", {
          ...recurrence.value,
          // @ts-ignore // FIXME:
          rule: { ...recurrence.value.rule, day },
        });
      },
    });
    // End date
    const endRadioChoice: Ref<"never" | "occurrence" | "date"> = ref("never");
    const occurrencesEnd = ref(1);
    watch(occurrencesEnd, () =>
      endRadioChoice.value === "occurrence" ? setEnd("occurrence") : null
    );
    const dateEnd = ref(new Date());
    watch(dateEnd, () =>
      endRadioChoice.value === "date" ? setEnd("date") : null
    );

    const setEnd = (type: "never" | "date" | "occurrence") => {
      switch (type) {
        case "never":
          endRadioChoice.value = "never";
          emit("update:recurrence", {
            ...recurrence.value,
            end: undefined,
          });
          break;
        case "date":
          endRadioChoice.value = "date";
          emit("update:recurrence", {
            ...recurrence.value,
            end: new Date(dateEnd.value),
          });
          break;
        case "occurrence":
          endRadioChoice.value = "occurrence";
          //FIXME - Update this when the back-end allows it
          //emit("update:recurrence", { ...recurrence.value, end: XXX, });
          break;
      }
    };
    return {
      dateEnd,
      dayOfTheMonth,
      eventDay,
      every,
      isWeekStartOnSunday,
      occurrencesEnd,
      period,
      queryWeekday,
      setEnd,
      weekdays,
    };
  },
});
</script>
<style lang="scss" scoped>
.recurrent-event-parameters {
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  @extend %font-caption-regular;
  @include themed() {
    color: t($primary);
  }
  input[type="number"] {
    border-radius: 3px;
    border: none;
    width: 30px;
    height: 24px;
    @extend %font-caption-regular;
    text-align: center;
    @include themed() {
      color: t($grey-70);
      background-color: t($grey-05);
    }
  }

  p {
    margin: 0;
  }
  .block {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    .line {
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 4px;
      &.disabled {
        @include themed() {
          color: t($grey-30);
        }
      }
    }
    :deep(.v-select.select) {
      height: 26px;
      @extend %font-caption-regular;
      .vs__dropdown-toggle {
        border: none;
        height: 100%;
        padding: 0;
        @include themed() {
          background-color: t($grey-05);
        }
        input {
          display: none;
        }

        .vs__selected-options {
          justify-content: center;
          padding: 0;

          .vs__selected {
            background-color: transparent;
            height: 100%;
            border: none;
            border-radius: 0;
            margin: 0;
            padding: 0;
            @include themed() {
              color: t($grey-70);
            }
          }
        }
        .vs__search {
          padding: 0;
        }
        .vs__actions {
          display: none;
        }
      }
      .vs__dropdown-menu {
        border-radius: 3px;
        min-width: 0;
        border: none;
        @include themed() {
          background-color: t($grey-05);
        }
        .vs__dropdown-option {
          padding: 8px 10px;
          @include themed() {
            color: t($grey-70);
          }
          &:hover {
            @include themed() {
              background-color: t($grey-15);
              color: t($primary);
            }
          }
        }
      }
    }

    &.every {
      :deep(.v-select.select) {
        .vs__dropdown-toggle {
          width: 64px;
        }
        .vs__dropdown-menu {
          width: 64px;
        }
      }
    }
    .day-of-the-week {
      display: flex;
      flex-direction: row;
      align-items: center;
      .day {
        margin-inline-start: -1px;
        display: flex;
        width: 36px;
        height: 30px;
        align-items: center;
        justify-content: center;
        @include themed() {
          border: 1px solid t($grey-20);
          color: t($grey-70);
        }
        &.clicked {
          @include themed() {
            background-color: t($grey-05);
            color: t($secondary);
          }
        }
        &:first-child {
          border-start-start-radius: 3px;
          border-end-start-radius: 3px;
        }
        &:last-child {
          border-start-end-radius: 3px;
          border-end-end-radius: 3px;
        }
        &:hover {
          @include themed() {
            background-color: t($grey-05);
          }
        }
      }
    }
    &.day-of-the-month {
      :deep(.v-select.select) {
        .vs__dropdown-toggle {
          width: 180px;
          .vs__selected-options {
            justify-content: start;
            padding: 4px 8px;
          }
        }
        .vs__dropdown-menu {
          width: 180px;
        }
      }
    }
    &.end {
      row-gap: 8px;
      .line {
        height: 22px;
        column-gap: 6px;
      }
      input[type="number"] {
        height: 20px;
      }
      input[type="date"] {
        border-radius: 3px;
        border: none;
        @extend %font-caption-regular;
        text-align: center;
        @include themed() {
          color: t($grey-70);
          background-color: t($grey-05);
        }
        &::-webkit-calendar-picker-indicator {
          opacity: 0.5;
        }
      }
      input[type="radio"] {
        filter: grayscale(1);
        margin: 0;
      }
    }
  }
}
</style>
